import { useMemo } from "react";
import {
  useScopedWayfinderSelection,
  WayfinderScope,
} from "hooks/useAdminGroup/useScopedWayfinderSelection";
import { useAdminGroupSubtreeIds } from "hooks/useAdminGroup/useAdminGroupSubtreeIds";

export interface ScopedLocationIds {
  isLbaEnabled: boolean;
  scopeKind: WayfinderScope["kind"];
  applyScope: boolean;
  /**
   * externalIds (as strings) of everything in the active scope: for a
   * location scope, that single location's id; for an admin-group scope, the
   * group's own externalId plus every descendant ADMIN_GROUP and LOCATION
   * externalId in its subtree.
   */
  scopeExternalIds: Set<string>;
  isLoading: boolean;
}

const EMPTY_IDS: Set<string> = new Set();

// Resolves the breadcrumb selection into the set of externalIds every
// KPI/chart fetch should be scoped to. LBA off or root selected => applyScope
// false (unfiltered, current behavior). An admin-group scope expands to its
// subtree's externalIds; a single-location scope is just that id.
export const useScopedLocationIds = (): ScopedLocationIds => {
  const { scope, isLbaEnabled, isFlagResolving } = useScopedWayfinderSelection();

  const { subtreeExternalIds, isReady } = useAdminGroupSubtreeIds(
    scope.kind === "ADMIN_GROUP" ? scope.id : undefined,
  );

  const applyScope = isLbaEnabled && scope.kind !== "NONE";

  const scopeExternalIds = useMemo<Set<string>>(() => {
    if (!applyScope) return EMPTY_IDS;
    if (scope.kind === "LOCATION") return new Set<string>([scope.id]);
    if (scope.kind === "ADMIN_GROUP") {
      return new Set<string>(subtreeExternalIds);
    }
    return EMPTY_IDS;
  }, [applyScope, scope, subtreeExternalIds]);

  // isFlagResolving holds consumers while the LBA flag is still resolving and
  // a stored selection exists -- otherwise the first paint would fire an
  // unscoped request and briefly render out-of-scope data before the flag
  // settles and the scoped refetch lands. An admin-group scope also waits on
  // the hierarchy; this cannot spin forever, because a failed hierarchy load
  // resolves the scope to NONE upstream (useScopedWayfinderSelection fails
  // open) and applyScope goes false.
  const isLoading =
    isFlagResolving ||
    (applyScope && scope.kind === "ADMIN_GROUP" && !isReady);

  return {
    isLbaEnabled,
    scopeKind: scope.kind,
    applyScope,
    scopeExternalIds,
    isLoading,
  };
};
